'use client'
import { PrismicImage, PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import CtaSimple from "@/components/CtaSimple";

import '@splidejs/react-splide/css/core';

/**
 * @typedef {import("@prismicio/client").Content.FeatureCardsSlice} FeatureCardsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FeatureCardsSlice>} FeatureCardsProps
 * @type {import("react").FC<FeatureCardsProps>}
 */

const Card = ({ item, idx, ref }) => (
  <div key={idx} className="bg-white rounded-2xl p-5 lg:pr-10 py-6 flex flex-col lg:flex-row h-full gap-6">
    <div className="h-[30px] w-[30px]">
      <PrismicImage
        field={item.picto}
        className="h-auto w-[30px] object-contain"
        loading="lazy"
      />
    </div>
    <div className="flex-1 flex flex-col gap-5">
      {isFilled.richText(item.card_title) && (
        <PrismicRichText
          field={item.card_title}
          components={{
            heading3: ({ children }) => (
              <h3 className="h3-sm">{children}</h3>
            ),
            heading4: ({ children }) => (
              <h4 className="h3-sm">{children}</h4>
            ),
            heading5: ({ children }) => (
              <h5 className="h3-sm">{children}</h5>
            ),
          }}
        />
      )}
      {isFilled.richText(item.card_description) && (
        <PrismicRichText
          field={item.card_description}
          components={{
            paragraph: ({ children }) => (
              <p className="">{children}</p>
            ),
          }}
        />
      )}
    </div>
  </div>
);



const FeatureCards = ({ slice }) => {
  const { title, cards, text, cta } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`framing py-20  bg-light-gray text-dark-green ${slice.variation == 'default' ? 'lg:py-30' : ''}`}
      aria-labelledby={`feature-cards-title-${slice.id || 'main'}`}
    >
      <div className="max-w-375 m-auto">
        {slice.variation == 'default' && <div className="flex flex-col gap-10">
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 id={`feature-cards-title-${slice.id || 'main'}`} className="h2-sm">{children}</h2>
                ),
                heading3: ({ children }) => (
                  <h3 className="h2-sm">{children}</h3>
                ),
                heading4: ({ children }) => (
                  <h4 className="h2-sm">{children}</h4>
                ),
              }}
            />
          )}
          {isFilled.group(cards) && (
            <div className="hidden md:block md:grid md:grid-cols-2 gap-10">
              {cards.map((item, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-5 lg:pr-10 py-6 flex flex-col lg:flex-row h-full gap-6">
                  <div className="h-[30px] w-[30px]">
                    <PrismicImage
                      field={item.picto}
                      className="h-auto w-[30px] object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-5">
                    {isFilled.richText(item.card_title) && (
                      <PrismicRichText
                        field={item.card_title}
                        components={{
                          heading3: ({ children }) => (
                            <h3 className="h3-sm">{children}</h3>
                          ),
                          heading4: ({ children }) => (
                            <h4 className="h3-sm">{children}</h4>
                          ),
                          heading5: ({ children }) => (
                            <h5 className="h3-sm">{children}</h5>
                          ),
                        }}
                      />
                    )}
                    {isFilled.richText(item.card_description) && (
                      <PrismicRichText
                        field={item.card_description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="">{children}</p>
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {isFilled.group(cards) && (
            <div className="md:hidden gap-10">
              <Splide
                options={{
                  type: "slide",
                  perPage: 1,
                  perMove: 1,
                  gap: "2.5rem",
                  arrows: false,
                  pagination: true,
                  speed: 800,
                }}
              >
                {cards.map((card, idx) => (
                  <SplideSlide key={idx}>
                    <Card item={card} idx={idx} />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}
        </div>
        }
        {slice.variation == 'inline' &&
          <div className="flex flex-col lg:flex-row gap-10 md:gap-20 lg:gap-35">
            <div className="lg:max-w-90 relative">
              <div className="lg:sticky top-40 flex flex-col gap-5">
                <img
                  src="/yy-green.svg"
                  alt="Logo Yes Yes Padel"
                  className="h-10 w-20 object-contain"
                  loading="lazy"
                />
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => (
                        <h2 className="h2-bold">{children}</h2>
                      ),
                      heading3: ({ children }) => (
                        <h3 className="h2-bold">{children}</h3>
                      ),
                      heading4: ({ children }) => (
                        <h4 className="h2-bold">{children}</h4>
                      ),
                    }}
                  />
                )}
                {isFilled.richText(text) && (
                  <PrismicRichText
                    field={text}
                    components={{
                      paragraph: ({ children }) => (
                        <p>{children}</p>
                      ),
                    }}
                  />
                )}
                {isFilled.link(cta) && (
                  <CtaSimple page={cta} padding={"px-5 hidden md:block"} width={"lg:w-full w-fit"} hover={"dark-green"} />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-10">
              {isFilled.group(cards) && (
                <div className="">
                  {cards.map((item, idx) => (
                    <div key={idx} className="first:pt-0 py-10 flex flex-col md:flex-row gap-6 border-b-1 border-green last:border-0 last:pb-0">
                      <div className="h-[30px] w-[30px]">
                        <PrismicImage
                          field={item.picto}
                          className="h-auto w-[30px] object-contain"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-5">
                        {isFilled.richText(item.card_title) && (
                          <PrismicRichText
                            field={item.card_title}
                            components={{
                              heading3: ({ children }) => (
                                <h3 className="h3-sm">{children}</h3>
                              ),
                              heading4: ({ children }) => (
                                <h4 className="h3-sm">{children}</h4>
                              ),
                              heading5: ({ children }) => (
                                <h5 className="h3-sm">{children}</h5>
                              ),
                            }}
                          />
                        )}
                        {isFilled.richText(item.card_description) && (
                          <PrismicRichText
                            field={item.card_description}
                            components={{
                              paragraph: ({ children }) => (
                                <p className="">{children}</p>
                              ),
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isFilled.link(cta) && (
                <CtaSimple page={cta} padding={"px-5 md:hidden"} width={"w-full md:w-fit"} hover={"dark-green"} />
              )}
            </div>
          </div>
        }
      </div>
    </section>
  );
};

export default FeatureCards;
